import { safeSetInnerHTML } from '@merlin/lib/js/trusted-types.js';
/**
 * Graph Explorer - Chat Module
 *
 * Replaces the old floating task-window system with a split-screen layout:
 *   Left (7 cols)  → Graph canvas (&amp; all existing toolbars / panels)
 *   Right (5 cols) → Lazy-loaded A2UIChat component
 *
 * Activated by the "Open Agents" toggle button injected into the task
 * toolbar area (right side of the header).
 *
 * Also exposes `openChatWithTask(taskId, query)` so that the search flow
 * in ExplorerAPI can open the chat panel directly instead of a floating card.
 *
 * Public API: window.ExplorerChat
 */

(function (global) {
    'use strict';

    // ============================================
    // Module State
    // ============================================
    const state = {
        isOpen: false,
        chatInstance: null,   // A2UIChat instance (lazy)
        chatModule: null,     // the ES module (lazy)
        isLoading: false,
        workspaceId: null,
        apiEndpoint: null,
        accessToken: null,
        cursorId: null,
        theme: 'dark',
    };

    // DOM references
    let toggleBtn = null;
    let chatContainer = null;

    // ============================================
    // Initialization
    // ============================================
    function init(options = {}) {
        console.log('[ExplorerChat] Initializing...');

        // Clean up any stale state from a previous init cycle
        // (e.g. MerlinGraph was destroyed + recreated on workspace switch)
        if (state.chatInstance) {
            try { state.chatInstance.destroy(); } catch (_) { /* ignore */ }
            state.chatInstance = null;
            state.chatModule = null;
        }
        state.isOpen = false;
        state.isLoading = false;

        // Absorb config from caller (MerlinGraph._initModules)
        state.apiEndpoint = options.apiEndpoint ?? null;
        state.workspaceId = options.workspaceId ?? null;
        state.cursorId    = options.cursorId ?? 'me';
        state.theme       = options.theme ?? 'dark';
        state.accessToken = options.accessToken ?? null;

        createToggleButton();
        createChatContainer();
        bindEvents();
        console.log('[ExplorerChat] Initialized', {
            workspaceId: state.workspaceId,
            apiEndpoint: state.apiEndpoint,
        });
    }

    // ============================================
    // Toggle Button (injected right of cursor bar)
    // ============================================
    function createToggleButton() {
        const graphExplorer = document.querySelector('.graph-explorer');
        if (!graphExplorer) return;

        // Remove previous instance
        document.getElementById('chat-toggle-bar')?.remove();

        const bar = document.createElement('nav');
        bar.id = 'chat-toggle-bar';
        bar.className = 'toolbar-chat-toggle';
        bar.setAttribute('role', 'toolbar');
        bar.setAttribute('aria-label', 'Chat controls');

        safeSetInnerHTML(bar, `
            <button id="chat-toggle-btn"
                    class="toolbar-icon-btn toolbar-chat-btn"
                    data-tooltip="Open Agents"
                    aria-label="Toggle chat panel"
                    aria-pressed="false">
                <i data-lucide="message-square"></i>
                <span class="toolbar-btn-label">Agents</span>
            </button>
        `);

        graphExplorer.appendChild(bar);
        toggleBtn = bar.querySelector('#chat-toggle-btn');

        if (typeof lucide !== 'undefined') {
            setTimeout(() => lucide.createIcons(), 10);
        }
    }

    // ============================================
    // Chat Panel Container
    // ============================================
    function createChatContainer() {
        const graphExplorer = document.querySelector('.graph-explorer');
        if (!graphExplorer) return;

        // Remove previous
        document.getElementById('explorer-chat-panel')?.remove();

        chatContainer = document.createElement('div');
        chatContainer.id = 'explorer-chat-panel';
        chatContainer.className = 'explorer-chat-panel';
        chatContainer.style.display = 'none';

        safeSetInnerHTML(chatContainer, `
            <div class="explorer-chat-header">
                <span class="explorer-chat-title">Agents</span>
                <button class="explorer-chat-close" id="chat-close-btn" title="Close chat panel">
                    <i data-lucide="x"></i>
                </button>
            </div>
            <div class="explorer-chat-body" id="explorer-chat-body">
                <!-- A2UIChat mounts here -->
            </div>
        `);

        graphExplorer.appendChild(chatContainer);

        if (typeof lucide !== 'undefined') {
            setTimeout(() => lucide.createIcons(), 10);
        }
    }

    // ============================================
    // Events
    // ============================================
    function bindEvents() {
        toggleBtn?.addEventListener('click', toggle);
        document.getElementById('chat-close-btn')?.addEventListener('click', close);

        // Listen for cursor changes from ExplorerCursor
        document.addEventListener('explorer:cursorChanged', (e) => {
            const { id } = e.detail || {};
            if (id && state.chatInstance) {
                state.cursorId = id;
                state.chatInstance.setCursor(id);
            }
        });
    }

    // ============================================
    // Open / Close / Toggle
    // ============================================
    function toggle() {
        if (state.isOpen) {
            close();
        } else {
            open();
        }
    }

    async function open() {
        if (state.isOpen) return;
        state.isOpen = true;

        const graphExplorer = document.querySelector('.graph-explorer');
        if (graphExplorer) graphExplorer.classList.add('chat-open');

        if (chatContainer) chatContainer.style.display = 'flex';
        if (toggleBtn) {
            toggleBtn.classList.add('active');
            toggleBtn.setAttribute('aria-pressed', 'true');
        }

        // Lazy-load A2UIChat on first open
        if (!state.chatInstance) {
            await loadChat();
        }

        // Notify vis.js after the CSS layout settles.  #mynetwork stays
        // position:absolute – only its `right` changes – so the canvas
        // just gets narrower.  A small delay ensures the browser has
        // painted the new layout before we ask vis.js to recalculate.
        _scheduleVisResize();
    }

    function close() {
        if (!state.isOpen) return;
        state.isOpen = false;

        const graphExplorer = document.querySelector('.graph-explorer');
        if (graphExplorer) graphExplorer.classList.remove('chat-open');

        if (chatContainer) chatContainer.style.display = 'none';
        if (toggleBtn) {
            toggleBtn.classList.remove('active');
            toggleBtn.setAttribute('aria-pressed', 'false');
        }

        _scheduleVisResize();
    }

    // ============================================
    // vis.js resize helper
    // ============================================
    let _visResizeTimer = null;

    /**
     * Wait for the CSS layout change to settle, then explicitly resize the
     * vis.js canvas to match the (possibly narrowed) #mynetwork container.
     *
     * IMPORTANT: we must use GraphExplorer.state.network – `GraphExplorer.network`
     * is not part of the public API and is always undefined.  Without calling
     * network.setSize() the vis.js <canvas> keeps its original full-page width
     * and all fit / focus / moveTo operations centre on the wrong origin.
     */
    function _scheduleVisResize() {
        clearTimeout(_visResizeTimer);
        _visResizeTimer = setTimeout(() => {
            const network = typeof GraphExplorer !== 'undefined' && GraphExplorer.state?.network;
            if (!network) return;

            // Explicitly tell vis.js about its new container dimensions.
            // A window 'resize' event alone is not reliable when only the
            // CSS `right` property changes (no window size change occurs).
            const container = document.getElementById('mynetwork');
            if (container && container.offsetWidth > 0) {
                network.setSize(container.offsetWidth + 'px', container.offsetHeight + 'px');
            }
            window.dispatchEvent(new Event('resize'));
            // Guard against the network being destroyed while this
            // timer was pending (e.g. modal closed before 150 ms).
            try { network.redraw(); } catch (_) { /* destroyed */ }
        }, 150);
    }

    // ============================================
    // Lazy-load A2UIChat
    // ============================================
    async function loadChat() {
        if (state.isLoading) return;
        state.isLoading = true;

        const body = document.getElementById('explorer-chat-body');
        if (!body) {
            state.isLoading = false;
            return;
        }

        safeSetInnerHTML(body, `
            <div class="explorer-chat-loading">
                <div class="streaming-dots"><span></span><span></span><span></span></div>
                <span>Loading chat...</span>
            </div>
        `);

        try {
            // Resolve auth token (same logic as ExplorerAPI / MerlinGraph)
            let token = state.accessToken;
            if (!token) {
                try {
                    const userStr = sessionStorage.getItem('user');
                    if (userStr) {
                        const user = JSON.parse(userStr);
                        token = user?.token || null;
                    }
                } catch (e) { /* ignore */ }
            }

            // Dynamic import – Vite resolves the relative path at build time
            const mod = await import('../../../chat/src/lib/A2UIChat.js');
            state.chatModule = mod;

            const A2UIChat = mod.A2UIChat || mod.default;

            state.chatInstance = new A2UIChat({
                apiEndpoint: state.apiEndpoint ?? '',
                workspaceId: state.workspaceId,
                canvasId: null,
                accessToken: token,
                layoutMode: 'compact',
                theme: state.theme,
                onEntityBadgeClick: handleEntityBadgeClick,
                // When the user clicks the clear-history button inside A2UIChat,
                // propagate the reset to the graph + cursor layer.
                onReset: handleChatReset,
                // When agent emits an explorer navigation directive and user clicks
                // the "Go to" button, navigate the explorer to the specified tab/object.
                onExplorerNavigate: navigateTo,
                // When agent finishes (messages injected), reload graph data so the
                // explorer reflects any changes the agent made.
                onDataChanged: () => smoothGraphRefresh(),
            });

            // Clear loading indicator and mount
            safeSetInnerHTML(body, '');
            await state.chatInstance.init(body);

            // Forward cursor
            if (state.cursorId) {
                state.chatInstance.setCursor(state.cursorId);
            }

            console.log('[ExplorerChat] A2UIChat mounted');
        } catch (err) {
            console.error('[ExplorerChat] Failed to load A2UIChat:', err);
            safeSetInnerHTML(body, `
                <div class="explorer-chat-error">
                    <i data-lucide="alert-triangle"></i>
                    <span>Failed to load chat component</span>
                    <button id="chat-retry-btn" class="toolbar-icon-btn">Retry</button>
                </div>
            `);
            document.getElementById('chat-retry-btn')?.addEventListener('click', () => {
                state.chatInstance = null;
                loadChat();
            });
            if (typeof lucide !== 'undefined') lucide.createIcons();
        } finally {
            state.isLoading = false;
        }
    }

    // ============================================
    // Entity Badge Click Bridge
    // ============================================
    /**
     * Called by A2UIChat when a user clicks an entity badge in a chat message.
     * Bridges to GraphExplorer so the node is selected + focused on the canvas.
     */
    function handleEntityBadgeClick(entityId, entityType, event) {
        if (!entityId) return;
        if (typeof GraphExplorer === 'undefined') {
            console.warn('[ExplorerChat] GraphExplorer not available');
            return;
        }

        const graphState = GraphExplorer.getState ? GraphExplorer.getState() : GraphExplorer.state;
        const nodes = graphState?.nodes;
        if (!nodes) return;

        const nodeData = nodes.get(entityId);
        if (!nodeData) {
            console.log('[ExplorerChat] Entity not in graph:', entityType, entityId);
            return;
        }

        const isMulti = event?.ctrlKey || event?.metaKey;

        if (isMulti) {
            const current = GraphExplorer.getSelectedNodes ? GraphExplorer.getSelectedNodes() : [];
            const idx = current.indexOf(entityId);
            const next = idx >= 0
                ? current.filter(id => id !== entityId)
                : [...current, entityId];
            GraphExplorer.highlightMultipleNodes(next);
        } else {
            GraphExplorer.selectNode(entityId, nodeData);
            GraphExplorer.focusNode(entityId, { scale: 1.2 });
        }
    }

    // ============================================
    // Chat Reset Bridge
    // ============================================
    /**
     * Called by A2UIChat (via onReset callback) when the user clicks the
     * clear-history button in the chat header.  Propagates the reset to
     * the cursor / graph layer so both sides stay in sync.
     */
    function handleChatReset() {
        console.log('[ExplorerChat] Chat reset requested — propagating to cursor');
        if (typeof ExplorerCursor !== 'undefined' && ExplorerCursor.resetCursorView) {
            ExplorerCursor.resetCursorView();
        }
    }

    // ============================================
    // Search Integration
    // ============================================
    /**
     * Open the chat panel, optionally switching to the task that came from a
     * search.  Called by ExplorerAPI after searchAsk completes.
     *
     * Uses openExternalTask (when available) so that tasks still being
     * processed show the streaming "..." indicator and stream the agent
     * response in real-time.
     *
     * @param {string} taskId  - A2A task ID returned by the search
     * @param {string} query   - Original search query
     */
    async function openChatWithTask(taskId, query) {
        await open();

        if (!state.chatInstance) {
            console.warn('[ExplorerChat] Chat instance not ready');
            return;
        }

        // Reload tasks so the new search task appears
        if (state.chatInstance._loadTasks) {
            await state.chatInstance._loadTasks();
        }

        // Strip [Label](type:id) markdown links from query for display name
        const displayQuery = query ? query.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') : '';
        const name = displayQuery ? `Search: ${displayQuery.substring(0, 20)}` : 'Search';

        // Prefer openExternalTask which handles in-progress tasks with
        // streaming indicator; fall back to _openTaskTab for backward compat.
        if (state.chatInstance.openExternalTask) {
            await state.chatInstance.openExternalTask(taskId, name);
        } else if (state.chatInstance._openTaskTab) {
            state.chatInstance._openTaskTab(taskId, name);
        }
    }

    // ============================================
    // External Setters (called by MerlinGraph)
    // ============================================
    function setChatWorkspace(workspaceId, apiEndpoint, accessToken) {
        state.workspaceId = workspaceId;
        if (apiEndpoint !== undefined) state.apiEndpoint = apiEndpoint;
        if (accessToken !== undefined) state.accessToken = accessToken;

        if (state.chatInstance?.setWorkspace) {
            state.chatInstance.setWorkspace(workspaceId, apiEndpoint, accessToken);
        }
    }

    function setChatTheme(theme) {
        state.theme = theme;
        if (state.chatInstance?.setTheme) {
            state.chatInstance.setTheme(theme);
        }
    }

    function setChatCursor(cursorId) {
        state.cursorId = cursorId;
        if (state.chatInstance?.setCursor) {
            state.chatInstance.setCursor(cursorId);
        }
    }

    /**
     * Full reset — clear chat messages, tasks, and cursor state.
     * Called by ExplorerCursor.resetCursorView() so the chat is
     * cleaned when the user resets via the cursor menu.
     *
     * Directly clears state without calling clearConversation() to
     * avoid the onReset → resetCursorView → resetChat loop.
     */
    function resetChat() {
        if (!state.chatInstance) return;

        // Wipe messages + task directly (bypasses onReset callback)
        state.chatInstance.messages = [];
        state.chatInstance.currentTaskId = null;
        if (state.chatInstance.tabs) {
            state.chatInstance.tabs = [{ id: 'main', label: 'New Chat', active: true }];
            state.chatInstance.activeTabId = 'main';
        }
        if (state.chatInstance._saveState) state.chatInstance._saveState();
        if (state.chatInstance._renderMessages) state.chatInstance._renderMessages();
        if (state.chatInstance._renderTasks) state.chatInstance._renderTasks();
        if (state.chatInstance._renderTabs) state.chatInstance._renderTabs();

        // Reload tasks / agents for the current workspace so stale
        // tasks from the previous cursor are removed.
        if (state.chatInstance.refresh) {
            state.chatInstance.refresh();
        }
    }

    function destroy() {
        // Cancel any pending vis.js resize timer so it doesn't
        // fire after the network has been torn down.
        clearTimeout(_visResizeTimer);
        _visResizeTimer = null;

        close();
        if (state.chatInstance?.destroy) {
            state.chatInstance.destroy();
        }
        state.chatInstance = null;
        state.chatModule = null;
        state.isOpen = false;
        state.isLoading = false;
        chatContainer?.remove();
        chatContainer = null;
        toggleBtn = null;
        document.getElementById('chat-toggle-bar')?.remove();
    }

    // ============================================
    // Explorer Navigation (Agent Directives)
    // ============================================
    /**
     * Navigate the explorer to a specific tab + object based on an
     * agent directive.  Called via onExplorerNavigate callback from A2UIChat.
     *
     * @param {Object} action - { tab, target?, action? }
     *   tab    — 'graph' | 'bookmarks' | 'timeline' | 'analytics' | 'artefacts'
     *   target — name of the checkpoint / bookmark / pointer (optional)
     *   action — verb: 'restore' | 'use' | 'preview' | 'open' (optional, default varies)
     */
    function navigateTo(action) {
        if (!action || !action.tab) {
            console.warn('[ExplorerChat] navigateTo: missing tab in action', action);
            return;
        }

        const tab = action.tab;
        const target = action.target;
        const verb = action.action;

        console.log('[ExplorerChat] navigateTo', { tab, target, verb });

        // 1. Switch explorer tab
        if (typeof ExplorerTabs !== 'undefined' && ExplorerTabs.switchTab) {
            ExplorerTabs.switchTab(tab);
        }

        // 2. If a target is specified, execute the corresponding action
        if (target && typeof ExplorerWindows !== 'undefined') {
            switch (tab) {
                case 'graph':
                    // Graph tab targets are checkpoints
                    if (verb === 'restore' || !verb) {
                        if (ExplorerWindows.restoreCheckpoint) {
                            ExplorerWindows.restoreCheckpoint(target);
                        }
                    } else if (verb === 'preview') {
                        if (ExplorerWindows.previewPointer) {
                            ExplorerWindows.previewPointer('checkpoint', target);
                        }
                    }
                    break;

                case 'bookmarks':
                    if (verb === 'use' || !verb) {
                        if (ExplorerWindows.useBookmark) {
                            ExplorerWindows.useBookmark(target);
                        }
                    } else if (verb === 'preview') {
                        if (ExplorerWindows.previewPointer) {
                            ExplorerWindows.previewPointer('bookmark', target);
                        }
                    }
                    break;

                case 'timeline':
                case 'analytics':
                case 'artefacts':
                    // These tabs don't have individual target navigation yet;
                    // switching to the tab is sufficient.
                    console.log(`[ExplorerChat] navigateTo: tab "${tab}" opened (no target action)`);
                    break;

                default:
                    console.warn('[ExplorerChat] navigateTo: unknown tab', tab);
            }
        }
    }

    // ============================================
    // Smooth Graph Refresh (carried over from ExplorerTasks)
    // ============================================
    function smoothGraphRefresh() {
        const refreshFn = () => {
            if (typeof ExplorerAPI !== 'undefined' && ExplorerAPI.refreshGraph) {
                console.log('[ExplorerChat] Smooth graph refresh');
                ExplorerAPI.refreshGraph().catch(err => {
                    console.warn('[ExplorerChat] Graph refresh failed:', err);
                });
            }
        };
        if (typeof requestIdleCallback !== 'undefined') {
            requestIdleCallback(refreshFn, { timeout: 1000 });
        } else {
            setTimeout(refreshFn, 100);
        }
    }

    // ============================================
    // Public API
    // ============================================
    global.ExplorerChat = {
        init,
        toggle,
        open,
        close,
        isOpen: () => state.isOpen,
        openChatWithTask,
        setChatWorkspace,
        setChatTheme,
        setChatCursor,
        resetChat,
        navigateTo,
        smoothGraphRefresh,
        destroy,
        /** Expose chat instance for cross-module coordination (e.g. task reload). */
        _getChatInstance: () => state.chatInstance,
    };

})(window);
